import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type WhatsappLiveChatModuleSpecsFragment } from '../../../../api/fragments/WhatsappLiveChatModuleSpecs';
import {
    UpdateWhatsappLiveChatModuleDocument,
    type UpdateWhatsappLiveChatModuleMutation,
    type UpdateWhatsappLiveChatModuleMutationVariables,
} from '../../../../api/mutations/updateWhatsappLiveChatModule';
import { type WhatsappLiveChatModuleInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type WhatsappLiveChatModuleUpdateFormProps = {
    module: WhatsappLiveChatModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = WhatsappLiveChatModuleInput;

const WhatsappLiveChatModuleUpdateForm = ({ module, onComplete }: WhatsappLiveChatModuleUpdateFormProps) => {
    const { t } = useTranslation(['whatsappLiveChatModuleDetails']);
    const apolloClient = useApolloClient();

    const moduleId = module.id;

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('whatsappLiveChatModuleDetails:updateDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateWhatsappLiveChatModuleMutation, UpdateWhatsappLiveChatModuleMutationVariables>({
                    mutation: UpdateWhatsappLiveChatModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('whatsappLiveChatModuleDetails:updateDrawer.messages.updateSuccess'),
                key: 'primary',
            });

            onComplete();
        },
        [apolloClient, moduleId, onComplete, t]
    );

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName'], module),
        }),
        [module]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateWhatsappLiveChatModule"
                    layout="vertical"
                    name="updateWhatsappLiveChatModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('whatsappLiveChatModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default WhatsappLiveChatModuleUpdateForm;
